import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import { Icon } from 'ui';
import { hasGlobalFeature } from 'selectors';
import * as actions from '../../../actions/pathBuilder/contents';
import * as selectors from '../../../selectors/pathBuilder';
import { pluralize } from '../../../utils/string_utils.js';
import Select from '../../shared/select';
import ContentWrapper from './ContentWrapper';
import ContentWarning from './Warning';
import Options from './Options';

class WaitStep extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      effect: props.effect,
      unit: props.unit,
      amount: props.amount,
      condition: props.condition,
    };
    this.getOptions = this.getOptions.bind(this);
    this.updateField = this.updateField.bind(this);
    this.saveChanges = this.saveChanges.bind(this);
  }

  static humanValues = {
    effect: {
      locked: 'locked',
      hidden: 'hidden',
    },
    unit: {
      days: 'days',
      weeks: 'weeks',
      months: 'months',
    },
    condition: {
      prevStepFinished: 'all previous steps are completed',
      pathAssigned: 'the path is assigned',
      certificationPathAssigned: t(
        'certifications.certification_builder.steps.waitstep.path_assigned',
      ),
    },
  };

  static defaultProps = {
    effect: 'locked',
    unit: 'days',
    condition: 'prevStepFinished',
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      effect: nextProps.effect,
      unit: nextProps.unit,
      amount: nextProps.amount,
      condition: nextProps.condition,
    });
  }

  updateField({ target }) {
    const { value, type, name } = target;
    this.setState({
      [name]: type === 'number' ? parseInt(value, 10) : value,
    });
  }

  saveChanges() {
    this.props.update(this.state);
    this.props.toggleEditing();

    if (this.props.justAdded) {
      this.props.persist();
    }
  }

  getOptions() {
    return [
      {
        label: 'Delete',
        icon: 'delete',
        onClick: this.props.remove,
      },
      {
        label: 'Edit',
        icon: 'edit',
        onClick: this.props.toggleEditing,
      },
    ];
  }

  getTextualSummary() {
    const { state: props } = this;
    const { humanValues } = WaitStep;
    return {
      condition: humanValues.condition[props.condition],
      effect: humanValues.effect[props.effect],
      amountAndUnit: `${props.amount} ${pluralize(
        props.amount,
        props.unit.slice(0, -1),
      )}`,
    };
  }

  setAmountInputRef(node) {
    if (node) node.focus();
  }

  render() {
    const {
      id,
      type,
      position,
      viewState,
      editingMode,
      validation,
      isCertification,
      features,
    } = this.props;
    const { effect, unit, amount, condition } = this.state;
    const { humanValues } = WaitStep;

    let textualSummary;
    if (viewState === 'normal') {
      textualSummary = this.getTextualSummary();
    }

    const EffectInput = (
      <Select
        className="ml-4 mr-4"
        name="effect"
        value={effect}
        onChange={this.updateField}
      >
        <option value="locked">{humanValues.effect.locked}</option>
        <option value="hidden">{humanValues.effect.hidden}</option>
      </Select>
    );

    const AmountInput = (
      <input
        className="NewInput NewInput--white w-60 mt-8 pr-0"
        ref={this.setAmountInputRef}
        min="1"
        name="amount"
        onChange={this.updateField}
        type="number"
        value={amount || ''}
      />
    );

    const UnitInput = (
      <Select
        className="mr-4"
        name="unit"
        value={unit}
        onChange={this.updateField}
      >
        <option value="days">{humanValues.unit.days}</option>
        <option value="weeks">{humanValues.unit.weeks}</option>
        <option value="months">{humanValues.unit.months}</option>
      </Select>
    );

    const ConditionInput = (
      <Select
        className="ml-4"
        name="condition"
        value={condition}
        onChange={this.updateField}
      >
        <option value="prevStepFinished">
          {humanValues.condition.prevStepFinished}
        </option>
        <option value="pathAssigned">
          {features.certifications && isCertification
            ? humanValues.condition.certificationPathAssigned
            : humanValues.condition.pathAssigned}
        </option>
      </Select>
    );

    const Bold = ({ children }) => (
      <b className="c-brand-gray-darkest">{children}</b>
    );

    return (
      <ContentWrapper {...{ id, type, position }} showStep={false}>
        <div className="PathContent WaitStep">
          <div className="PathContent-header">
            <div className="PathContent-title">
              <Icon name="waitstep" className="PathContent-icon" />
              <span
                className={
                  viewState === 'normal' && validation.invalid
                    ? 'opacity-50'
                    : ''
                }
              >
                Waitstep
              </span>
            </div>
            {viewState === 'normal' && editingMode && (
              <Options options={this.getOptions()} />
            )}
            {viewState === 'editing' && (
              <div className="PathContentOptions">
                <button
                  type="button"
                  className="u-linkBtn u-linkBtn--dark td-u"
                  children="Cancel"
                  onClick={this.props.cancelEditing}
                />
                <button
                  type="button"
                  className="NewButton NewButton--blue NewButton--wide ml-16"
                  disabled={!amount}
                  onClick={this.saveChanges}
                  children="Done"
                />
              </div>
            )}
          </div>
          {viewState === 'normal' && (
            <div className="PathContent-body c-brand-gray-darker">
              <div className={validation.invalid ? 'opacity-50' : ''}>
                The following step will be <Bold>{textualSummary.effect}</Bold>{' '}
                until 12:00am EST,&nbsp;
                <Bold>{textualSummary.amountAndUnit}</Bold> after{' '}
                <Bold>{textualSummary.condition}</Bold>
              </div>
              {validation.invalid && (
                <ContentWarning message={validation.message} marginTop />
              )}
            </div>
          )}
          {viewState === 'editing' && (
            <div className="PathContent-body c-brand-gray-darker">
              The following steps will be {EffectInput} until 12:00am EST,{' '}
              <br />
              {AmountInput} {UnitInput} after {ConditionInput}
            </div>
          )}
        </div>
      </ContentWrapper>
    );
  }
}

const mapStateToProps = () => {
  const isEditing = selectors.isEditingContent();
  const isJustAdded = selectors.isJustAdded();
  const validate = selectors.validateWaitStep();
  return (state, props) => ({
    validation: validate(state.pathBuilder, props),
    editingMode: selectors.modeSelectors.isEdit(state.pathBuilder),
    viewState: isEditing(state.pathBuilder, props) ? 'editing' : 'normal', // TODO refactor
    justAdded: isJustAdded(state.pathBuilder, props),
    isCertification: !!get('certification')(state.pathBuilder),
    features: {
      certifications: hasGlobalFeature(state, 'certifications'),
    },
  });
};

const mapDispatchToProps = (dispatch, { id, type, position, justAdded }) => ({
  update: change => dispatch(actions.update(id, type, change)),
  persist: () => dispatch(actions.persist(id, type)),
  remove: () => dispatch(actions.remove(id, type)),
  toggleEditing: () => dispatch(actions.toggleEditing(id, type)),
  cancelEditing: () => dispatch(actions.cancelEditing(id, type, justAdded)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WaitStep);
